import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { validateCpf, formatCpfNumber } from "@/app/utils/cpfFormater";
import { useMutation } from "@tanstack/react-query";
import { crefisaService } from "@/app/services/crefisaService";
import { useCrefisaContext } from "@/app/contexts/CrefisaContext";
import { useEffect, useState } from "react";
import { useToast } from "@/view/components/ui/use-toast";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import masks from "@/app/utils/masks";

const OVERDUE_CONTRACT_MESSAGE =
  "Cliente com contrato em atraso. Não é possível atender solicitação de empréstimo";

const getGenerateProposalSchema = z.object({
  cpf: z
    .string()
    .min(11, "Campo obrigatório")
    .refine((v) => validateCpf(v), "CPF Inválido")
    .transform((f) => f.replace(/\D/g, "")),
  phone: z
    .string({
      required_error: "Campo obrigatório",
    })
    .min(10, "Telefone inválido")
    .transform((v) => v.replace(/\D/g, "")),
});

type getGenerateProposalFormData = z.infer<typeof getGenerateProposalSchema>;

interface UseGenerateProposalModalProps {
  onContinue?: () => void;
  onContinuePreApproved?: () => void;
}

export default function useGenerateProposalModal({
  onContinue,
  onContinuePreApproved,
}: UseGenerateProposalModalProps) {
  const { toast } = useToast();
  const { setSimulation, setCustomer, setProposal, customerData } =
    useCrefisaContext();

  const [isLoading, setIsLoading] = useState(false);

  const getGenerateProposalForm = useForm<getGenerateProposalFormData>({
    resolver: zodResolver(getGenerateProposalSchema),
    mode: "onChange",
    defaultValues: {
      cpf: customerData?.cpf ? formatCpfNumber(customerData.cpf) : "",
      phone: "",
    },
  });

  const { handleSubmit, watch, setValue, getValues } = getGenerateProposalForm;

  const cpfValue = watch("cpf");
  const cleanCpf = (cpfValue ?? "").replace(/\D/g, "");

  const { data: existingCustomer } = useCustomerCpf(cleanCpf);

  useEffect(() => {
    if (existingCustomer?.phone && !getValues("phone")) {
      setValue("phone", masks.phone(existingCustomer.phone), {
        shouldValidate: true,
      });
    }
  }, [existingCustomer, getValues, setValue]);

  const { mutateAsync: getOperations, isPending: isLoadingOperations } =
    useMutation({
      mutationFn: (cpf: string) => crefisaService.getOperations(cpf),
    });

  const submitModal = handleSubmit(async (data) => {
    try {
      setIsLoading(true);

      const operations = await getOperations(data.cpf);

      if (!operations.clienteId?.trim()) {
        const message =
          operations.mensagem === OVERDUE_CONTRACT_MESSAGE
            ? "Cliente sem Perfil"
            : operations.mensagem;

        toast({
          title: "Erro ao gerar proposta",
          description: message || "Não foi possível buscar simulações",
          variant: "destructive",
        });

        return;
      }

      setCustomer({
        cpf: data.cpf,
        phone: data.phone,
      });

      setSimulation({
        clientId: operations.clienteId,
        isRefinancing: operations.permiteCapturaRefin,
        borrowedValue: 0,
        preApproveOffers: operations.ofertasPreAprovadas ?? [],
      });

      const firstOffer = operations.ofertasPreAprovadas?.[0];

      if (firstOffer) {
        setProposal({
          offerId: firstOffer.idOferta,
          value: firstOffer.valorSimulacao,
          prazo: firstOffer.quantidadeParcelas,
        });

        onContinuePreApproved?.();
        return;
      }

      onContinue?.();
    } catch (error) {
      toast({
        title: "Erro ao gerar proposta",
        description: "Não foi possível buscar simulações",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  });

  return {
    form: getGenerateProposalForm,
    submitModal,
    loading: isLoadingOperations || isLoading,
  };
}
